/**
 * Centralized API Client
 *
 * Provides consistent fetch wrappers with automatic:
 * - Authorization headers (JWT token)
 * - Credentials for cookies
 * - Content-Type headers for JSON bodies
 * - Error handling with detailed ApiError
 */

import { getAccessToken } from "@/contexts/AuthContext";
import { createApiError } from "./errors";

class ApiClient {
  private getHeaders(includeContentType = false): HeadersInit {
    const token = getAccessToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  }

  async get<T>(url: string): Promise<T> {
    const response = await fetch(url, {
      credentials: "include",
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw await createApiError(response);
    }

    return response.json();
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: this.getHeaders(!!data),
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      throw await createApiError(response);
    }

    return response.json();
  }

  async patch<T>(url: string, data: unknown): Promise<T> {
    const response = await fetch(url, {
      method: "PATCH",
      credentials: "include",
      headers: this.getHeaders(true),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw await createApiError(response);
    }

    return response.json();
  }

  async delete(url: string): Promise<void> {
    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw await createApiError(response);
    }
  }

  /**
   * Raw request method that returns Response.
   * Used for backwards compatibility with apiRequest() in queryClient.
   */
  async request(method: string, url: string, data?: unknown): Promise<Response> {
    const hasBody = data !== undefined;
    const response = await fetch(url, {
      method,
      credentials: "include",
      headers: this.getHeaders(hasBody),
      body: hasBody ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      throw await createApiError(response);
    }

    return response;
  }
}

export const api = new ApiClient();
